const BASE = '';

function getLocale() {
  try { return localStorage.getItem('envol_lang') || 'fr'; } catch { return 'fr'; }
}

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-locale': getLocale(),
      ...(options.headers || {}),
    },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || `HTTP ${res.status}`), { status: res.status, body });
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Auth ────────────────────────────────────────────────────────────────────

export async function getSession() {
  return request('/api/auth/session');
}

export async function getCsrfToken() {
  const data = await request('/api/auth/csrf');
  return data.csrfToken;
}

export async function signIn(email, password) {
  const csrfToken = await getCsrfToken();
  await fetch('/api/auth/callback/credentials', {
    method: 'POST',
    credentials: 'include',
    redirect: 'manual',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ csrfToken, email, password, redirect: 'false', callbackUrl: '/' }),
  });
  const session = await getSession();
  if (!session?.user) throw new Error('Email ou mot de passe incorrect.');
  return session.user;
}

export async function signOut() {
  const csrfToken = await getCsrfToken();
  await fetch('/api/auth/signout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ csrfToken, callbackUrl: '/' }),
  });
}

// ── Candidatures ─────────────────────────────────────────────────────────────

export function submitCandidature(data) {
  return request('/api/candidatures', { method: 'POST', body: JSON.stringify(data) });
}

// ── Contact ──────────────────────────────────────────────────────────────────

export function sendContactMessage(data) {
  return request('/api/contact', { method: 'POST', body: JSON.stringify(data) });
}

// ── Newsletter ───────────────────────────────────────────────────────────────

export async function subscribeNewsletter(email, prenom) {
  const res = await request('/api/newsletter', { method: 'POST', body: JSON.stringify({ email, ...(prenom ? { prenom } : {}) }) });
  if (res?.message === 'Déjà abonné') {
    throw Object.assign(new Error('Cette adresse est déjà inscrite à notre newsletter.'), { code: 'ALREADY_SUBSCRIBED' });
  }
  return res;
}

// ── Articles → { articles, total, page, pages } ──────────────────────────────

export function getArticles(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/contenu/articles${qs ? `?${qs}` : ''}`);
}

export function getArticle(slugOrId) {
  return request(`/api/contenu/articles/${slugOrId}`);
}

// ── Événements → { evenements, total, page, pages } ─────────────────────────

export function getEvenements(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/contenu/evenements${qs ? `?${qs}` : ''}`);
}

export function getEvenement(id) {
  return request(`/api/contenu/evenements/${id}`);
}

export function inscrireEvenement(id, data) {
  return request(`/api/contenu/evenements/${id}/inscription`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getInscriptions(evenementId) {
  return request(`/api/contenu/evenements/${evenementId}/inscription`);
}

export function deleteInscription(evenementId, inscId) {
  return request(`/api/contenu/evenements/${evenementId}/inscription/${inscId}`, { method: 'DELETE' });
}

// ── Ressources → { ressources, total, page, pages } ─────────────────────────

export function getRessources(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/contenu/ressources${qs ? `?${qs}` : ''}`);
}

// ── FAQ → array ──────────────────────────────────────────────────────────────

export function getFaq(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/contenu/faq${qs ? `?${qs}` : ''}`);
}

// ── Dashboard → { kpis, repartitionSecteur, repartitionZone, indicateurs } ──

export function getDashboard() {
  return request('/api/impact/dashboard');
}

// ── Indicateurs → array ──────────────────────────────────────────────────────

export function getIndicateurs(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/impact/indicateurs${qs ? `?${qs}` : ''}`);
}

// ── Rapports → array ─────────────────────────────────────────────────────────

export function getRapports(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/impact/rapports${qs ? `?${qs}` : ''}`);
}

// ── Partenaires → array ──────────────────────────────────────────────────────

export function getPartenaires(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/partenaires${qs ? `?${qs}` : ''}`);
}

// ── Appel actif public ───────────────────────────────────────────────────────

export function getAppelActif() {
  return request('/api/candidatures/appel-actif');
}

// Liste publique des appels OUVERT + FERMÉ (historique cohortes)
export function getAppelsPublics() {
  return request('/api/candidatures/appels/publics');
}

// ── Admin — Formulaires de candidature ───────────────────────────────────────

export function getFormulaires() {
  return request('/api/candidatures/formulaires');
}

export function createFormulaire(data) {
  return request('/api/candidatures/formulaires', { method: 'POST', body: JSON.stringify(data) });
}

export function updateFormulaire(id, data) {
  return request(`/api/candidatures/formulaires/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteFormulaire(id) {
  return request(`/api/candidatures/formulaires/${id}`, { method: 'DELETE' });
}

// ── Admin — Appels à candidatures ────────────────────────────────────────────

export function getAppels() {
  return request('/api/candidatures/appels');
}

export function createAppel(data) {
  return request('/api/candidatures/appels', { method: 'POST', body: JSON.stringify(data) });
}

export function updateAppel(id, data) {
  return request(`/api/candidatures/appels/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteAppel(id) {
  return request(`/api/candidatures/appels/${id}`, { method: 'DELETE' });
}

// ── Admin — Candidatures ─────────────────────────────────────────────────────

export function createParticipante(data) {
  return request('/api/candidatures/admin', { method: 'POST', body: JSON.stringify(data) });
}

export function getCandidaturesAdmin(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/candidatures${qs ? `?${qs}` : ''}`);
}

// Supprime toutes les candidatures d'un appel. appelId='__none__' = sans appel.
export function deleteCandidaturesBulk(appelId) {
  return request(`/api/candidatures/bulk?appelId=${encodeURIComponent(appelId)}`, { method: 'DELETE' });
}

export function getCandidatureStats(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/candidatures/stats${qs ? `?${qs}` : ''}`);
}

// renvoyer: true force un nouvel envoi aux candidates déjà notifiées.
export function notifySelectedCandidates({ ids, cohorteId, appelId, renvoyer }) {
  return request('/api/candidatures/notify-selected', {
    method: 'POST',
    body: JSON.stringify({ ...(ids ? { ids } : { cohorteId, appelId }), ...(renvoyer ? { renvoyer: true } : {}) }),
  });
}

export function updateCandidatureStatut(id, statut, noteEvaluateur, cohorteId) {
  return request(`/api/candidatures/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      statut,
      ...(noteEvaluateur ? { noteEvaluateur } : {}),
      ...(cohorteId !== undefined ? { cohorteId } : {}),
    }),
  });
}

export function getCandidature(id) {
  return request(`/api/candidatures/${id}`);
}

export function deleteCandidature(id) {
  return request(`/api/candidatures/${id}`, { method: 'DELETE' });
}

export function updateParticipanteProfil(id, data) {
  return request(`/api/candidatures/${id}/profil`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ── Admin — Articles CRUD ────────────────────────────────────────────────────

export function createArticle(data) {
  return request('/api/contenu/articles', { method: 'POST', body: JSON.stringify(data) });
}

export function updateArticle(id, data) {
  return request(`/api/contenu/articles/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteArticle(id) {
  return request(`/api/contenu/articles/${id}`, { method: 'DELETE' });
}

// ── Admin — Événements CRUD ──────────────────────────────────────────────────

export function createEvenement(data) {
  return request('/api/contenu/evenements', { method: 'POST', body: JSON.stringify(data) });
}

export function updateEvenement(id, data) {
  return request(`/api/contenu/evenements/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteEvenement(id) {
  return request(`/api/contenu/evenements/${id}`, { method: 'DELETE' });
}

// ── Annuaire public ──────────────────────────────────────────────────────────

export function getAnnuaire(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/annuaire${qs ? `?${qs}` : ''}`);
}

// ── Liens de profil auto-servis (sans compte) ─────────────────────────────────

export function sendProfilLinks({ ids, cohorteId, appelId } = {}) {
  return request('/api/candidatures/send-profil-links', {
    method: 'POST',
    body:   JSON.stringify(ids ? { ids } : { cohorteId, appelId }),
  });
}

// ── Admin — Cohortes CRUD ────────────────────────────────────────────────────

export function getProgrammes() {
  return request('/api/programmes');
}

export function getCohorte(id) {
  return request(`/api/programmes/cohortes/${id}`);
}

export function getCohorteParticipantes(id) {
  return request(`/api/programmes/cohortes/${id}/participantes`);
}

export function getCohortes(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/programmes/cohortes${qs ? `?${qs}` : ''}`);
}

export function createCohorte(data) {
  return request('/api/programmes/cohortes', { method: 'POST', body: JSON.stringify(data) });
}

export function updateCohorte(id, data) {
  return request(`/api/programmes/cohortes/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteCohorte(id) {
  return request(`/api/programmes/cohortes/${id}`, { method: 'DELETE' });
}

// ── Admin — Utilisateurs ─────────────────────────────────────────────────────

export function getUtilisateursAdmin(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/utilisateurs${qs ? `?${qs}` : ''}`);
}

export function createUtilisateurAdmin(data) {
  return request('/api/utilisateurs', { method: 'POST', body: JSON.stringify(data) });
}

export function updateUtilisateurAdmin(id, data) {
  return request(`/api/utilisateurs/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteUtilisateurAdmin(id) {
  return request(`/api/utilisateurs/${id}`, { method: 'DELETE' });
}

// ── Admin — Messages ─────────────────────────────────────────────────────────

export function getMessages(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/messages${qs ? `?${qs}` : ''}`);
}

export function updateMessage(id, data) {
  return request(`/api/messages/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteMessage(id) {
  return request(`/api/messages/${id}`, { method: 'DELETE' });
}

export function replyMessage(id, reponse) {
  return request(`/api/messages/${id}/reply`, { method: 'POST', body: JSON.stringify({ reponse }) });
}

// ── Admin — Newsletter ───────────────────────────────────────────────────────

export function getAbonnesAdmin(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/newsletter/admin${qs ? `?${qs}` : ''}`);
}

export function unsubscribeNewsletter(email) {
  return request('/api/newsletter', { method: 'DELETE', body: JSON.stringify({ email }) });
}

// ── Newsletter — Campagnes ────────────────────────────────────────────────────

export function getCampagnes() {
  return request('/api/newsletter/campagnes');
}

export function createCampagne(data) {
  return request('/api/newsletter/campagnes', { method: 'POST', body: JSON.stringify(data) });
}

export function deleteCampagne(id) {
  return request(`/api/newsletter/campagnes/${id}`, { method: 'DELETE' });
}

export function envoyerCampagne(id) {
  return request(`/api/newsletter/campagnes/${id}/envoyer`, { method: 'POST' });
}

// ── Témoignages ──────────────────────────────────────────────────────────────

export function getTemoignages() {
  return request('/api/temoignages');
}

export function getTemoignagesAdmin() {
  return request('/api/temoignages/admin');
}

export function createTemoignage(data) {
  return request('/api/temoignages/admin', { method: 'POST', body: JSON.stringify(data) });
}

export function updateTemoignage(id, data) {
  return request(`/api/temoignages/admin/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function deleteTemoignage(id) {
  return request(`/api/temoignages/admin/${id}`, { method: 'DELETE' });
}

// ── Upload fichier ────────────────────────────────────────────────────────────

export async function uploadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch('/api/upload', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// Upload vidéo — endpoint dédié avec limite 200 Mo, avec suivi de progression
export async function uploadVideo(file, onProgress) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/upload/video');
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }
    xhr.onload = () => {
      const body = (() => { try { return JSON.parse(xhr.responseText); } catch { return {}; } })();
      if (xhr.status >= 200 && xhr.status < 300) resolve(body);
      else reject(new Error(body.error || `HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Erreur réseau lors de l\'upload.'));
    xhr.send(formData);
  });
}

// POST /api/candidatures/import — import Excel/CSV de candidatures (ADMIN/STAFF)
// Retourne { created, skipped, errors: [{row, message}] }
export async function importCandidaturesExcel(file, appelId) {
  const formData = new FormData();
  formData.append('file', file);
  if (appelId) formData.append('appelId', appelId);
  const res = await fetch('/api/candidatures/import', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = Object.assign(new Error(body.error || `HTTP ${res.status}`), { status: res.status, body });
    throw err;
  }
  return body; // { created, skipped, errors }
}

// ── Galerie / Médiathèque ────────────────────────────────────────────────────

export function getGaleries(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/galeries${qs ? `?${qs}` : ''}`);
}

export function getGalerie(id) {
  return request(`/api/galeries/${id}`);
}

export function createGalerie(data) {
  return request('/api/galeries', { method: 'POST', body: JSON.stringify(data) });
}

export function updateGalerie(id, data) {
  return request(`/api/galeries/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteGalerie(id) {
  return request(`/api/galeries/${id}`, { method: 'DELETE' });
}

export function addMedia(galerieId, data) {
  return request(`/api/galeries/${galerieId}/medias`, { method: 'POST', body: JSON.stringify(data) });
}

export function deleteMedia(galerieId, mediaId) {
  return request(`/api/galeries/${galerieId}/medias/${mediaId}`, { method: 'DELETE' });
}

// ── Galeries cohorte ──────────────────────────────────────────────────────────

export function getCohorteGaleries(cohorteId) {
  return request(`/api/programmes/cohortes/${cohorteId}/galeries`);
}

export function createCohorteGalerie(cohorteId, categorie) {
  return request(`/api/programmes/cohortes/${cohorteId}/galeries`, {
    method: 'POST',
    body: JSON.stringify({ categorie }),
  });
}

// ── Commentaires ─────────────────────────────────────────────────────────────

export function getCommentaires(slugOrId, opts = {}) {
  const qs = opts.all ? '?all=true' : '';
  return request(`/api/contenu/articles/${slugOrId}/commentaires${qs}`);
}

export function createCommentaire(slugOrId, data) {
  return request(`/api/contenu/articles/${slugOrId}/commentaires`, { method: 'POST', body: JSON.stringify(data) });
}

export function approuverCommentaire(id, approuve) {
  return request(`/api/contenu/commentaires/${id}`, { method: 'PATCH', body: JSON.stringify({ approuve }) });
}

export function signalerCommentaire(id) {
  return request(`/api/contenu/commentaires/${id}/signaler`, { method: 'POST' });
}

export function patchCommentaire(id, data) {
  return request(`/api/contenu/commentaires/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteCommentaire(id) {
  return request(`/api/contenu/commentaires/${id}`, { method: 'DELETE' });
}

export function getCommentairesAdmin(opts = {}) {
  const params = new URLSearchParams();
  if (opts.signale  !== undefined) params.set('signale',  String(opts.signale));
  if (opts.approuve !== undefined) params.set('approuve', String(opts.approuve));
  if (opts.q)                      params.set('q', opts.q);
  const qs = params.toString() ? `?${params}` : '';
  return request(`/api/contenu/commentaires${qs}`);
}

// ── Notifications temps réel ──────────────────────────────────────────────────

export function getNotifications() {
  return request('/api/notifications');
}

export function markNotifRead(id) {
  return request(`/api/notifications/${id}`, { method: 'PATCH' });
}

export function markAllNotifsRead() {
  return request('/api/notifications/lire-tout', { method: 'POST' });
}

// ── Paramètres du site ────────────────────────────────────────────────────────

export async function getParametres() {
  const r = await fetch('/api/parametres', { credentials: 'include' });
  return r.json();
}

export function updateParametres(data) {
  return request('/api/parametres', { method: 'PUT', body: JSON.stringify(data) });
}

// ── Emails automatiques ───────────────────────────────────────────────────────

export function getEmailTemplates() {
  return request('/api/emails/templates');
}

export function saveEmailTemplate(cle, { champs, actif }) {
  return request(`/api/emails/templates/${cle}`, { method: 'PUT', body: JSON.stringify({ champs, actif }) });
}

export function resetEmailTemplate(cle) {
  return request(`/api/emails/templates/${cle}`, { method: 'DELETE' });
}

export function previewEmailTemplate(cle, champs) {
  return request(`/api/emails/templates/${cle}/apercu`, { method: 'POST', body: JSON.stringify({ champs }) });
}

export function sendTestEmailTemplate(cle, { champs, to }) {
  return request(`/api/emails/templates/${cle}/test`, {
    method: 'POST',
    body: JSON.stringify({ champs, ...(to ? { to } : {}) }),
  });
}

// ── Traductions dynamiques ────────────────────────────────────────────────────

export function getTranslationStatus(entityType, locale = 'en') {
  return request(`/api/translations/status?entityType=${entityType}&locale=${locale}`);
}

export function getEntityTranslations(entityType, entityId, locale = 'en') {
  return request(`/api/translations?entityType=${entityType}&entityId=${entityId}&locale=${locale}`);
}

export function saveTranslations(entityType, entityId, locale, translations) {
  return request('/api/translations', {
    method: 'PUT',
    body: JSON.stringify({ entityType, entityId, locale, translations }),
  });
}

export function deleteTranslations(entityType, entityId, locale) {
  return request(`/api/translations?entityType=${entityType}&entityId=${entityId}&locale=${locale}`, {
    method: 'DELETE',
  });
}

export function autoTranslate(text, from = 'fr', to = 'en') {
  return request('/api/translations/auto', {
    method: 'POST',
    body: JSON.stringify({ text, from, to }),
  });
}

// ── Templates d'enquête ─────────────────────────────────────────────────────

export function getTemplatesEnquete() {
  return request('/api/templates-enquete');
}

export function getTemplateEnquete(id) {
  return request(`/api/templates-enquete/${id}`);
}

export function createTemplateEnquete(data) {
  return request('/api/templates-enquete', { method: 'POST', body: JSON.stringify(data) });
}

export function updateTemplateEnquete(id, data) {
  return request(`/api/templates-enquete/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteTemplateEnquete(id) {
  return request(`/api/templates-enquete/${id}`, { method: 'DELETE' });
}

// ── Enquêtes de satisfaction ────────────────────────────────────────────────

export function getEnquetes(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/enquetes${qs ? '?' + qs : ''}`);
}

export function getEnquete(id) {
  return request(`/api/enquetes/${id}`);
}

export function createEnquete(data) {
  return request('/api/enquetes', { method: 'POST', body: JSON.stringify(data) });
}

export function updateEnquete(id, data) {
  return request(`/api/enquetes/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteEnquete(id) {
  return request(`/api/enquetes/${id}`, { method: 'DELETE' });
}

export function getResultatsEnquete(id) {
  return request(`/api/enquetes/${id}/resultats`);
}

export function envoyerEnquete(id, data) {
  return request(`/api/enquetes/${id}/envoyer`, { method: 'POST', body: JSON.stringify(data) });
}

// ── Réponse publique (sans auth) ────────────────────────────────────────────

export function getEnquetePublique(token) {
  return fetch(`/api/enquetes/public/${token}`, {
    headers: { 'x-locale': (() => { try { return localStorage.getItem('envol_lang') || 'fr'; } catch { return 'fr'; } })() },
  }).then(async res => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(body.error || `HTTP ${res.status}`), { status: res.status, body });
    return body;
  });
}

export function soumettreEnquetePublique(token, items) {
  return fetch(`/api/enquetes/public/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  }).then(async res => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(body.error || `HTTP ${res.status}`), { status: res.status, body });
    return body;
  });
}

